import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api';

const INACTIVITY_LIMIT = 30 * 60 * 1000; // 30 minutes in milliseconds
const WARNING_LIMIT = 60 * 1000; // 60 seconds warning countdown

export default function InactivityHandler({ children }) {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const navigate = useNavigate();
  const location = useLocation();

  const idleTimer = useRef(null);
  const warningTimer = useRef(null);
  const countdownInterval = useRef(null);

  // Check if user is logged in
  const isLoggedIn = !!localStorage.getItem('token');

  // Reset the main idle timer
  const resetTimer = () => {
    if (showWarning) return; // Do not reset if warning is already visible

    if (idleTimer.current) clearTimeout(idleTimer.current);

    idleTimer.current = setTimeout(() => {
      triggerWarning();
    }, INACTIVITY_LIMIT);
  };

  const triggerWarning = () => {
    if (!isLoggedIn) return;
    setShowWarning(true);
    setCountdown(60);

    // Setup 60s countdown interval
    countdownInterval.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval.current);
          handleLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleLogout = async () => {
    setShowWarning(false);
    clearInterval(countdownInterval.current);
    if (warningTimer.current) clearTimeout(warningTimer.current);

    try {
      // Direct logout API call
      await api.post('/auth/logout', {}).catch(() => {});
    } catch (_) {}

    // Clear client side credentials
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.clear();
    navigate('/');
    Modal.info({
      title: 'Logged Out',
      content: 'You have been logged out due to inactivity to protect your account.',
      okText: 'Login Again',
    });
  };

  const keepSessionAlive = () => {
    setShowWarning(false);
    clearInterval(countdownInterval.current);
    resetTimer();

    // Ping the backend to slide activity timestamp
    api.get('/settings/sessions').catch(() => {});
  };

  useEffect(() => {
    // Events to track user interaction
    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll', 'click'];

    if (isLoggedIn && location.pathname !== '/') {
      resetTimer();
      events.forEach((event) => {
        window.addEventListener(event, resetTimer);
      });
    }

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (warningTimer.current) clearTimeout(warningTimer.current);
      if (countdownInterval.current) clearInterval(countdownInterval.current);
    };
  }, [isLoggedIn, location.pathname, showWarning]);

  return (
    <>
      {children}
      <Modal
        title="Session Timeout Warning"
        open={showWarning}
        closable={false}
        maskClosable={false}
        footer={[
          <Button key="logout" type="text" danger onClick={handleLogout}>
            Logout Now
          </Button>,
          <Button key="keep" type="primary" onClick={keepSessionAlive}>
            Keep Me Logged In
          </Button>,
        ]}
      >
        <p>You have been inactive for a while.</p>
        <p style={{ fontWeight: 'bold', fontSize: '16px', color: '#ff4d4f' }}>
          You will be logged out in {countdown} seconds due to security policy.
        </p>
      </Modal>
    </>
  );
}
